import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useMasterData } from "@/hooks/useMasterData";
import AIPrioritySuggest from "@/components/AIPrioritySuggest";
import AskAI from "@/components/AskAI";
import Documents from "@/components/Documents";
import CandidateCredentials from "@/components/CandidateCredentials";
import RecommendedJobsPanel from "@/components/RecommendedJobsPanel";
import CandidateApplicationsPanel from "@/components/CandidateApplicationsPanel";
import CandidatePortalAccess from "@/components/CandidatePortalAccess";
import HoldResumeControl from "@/components/HoldResumeControl";
import EditableField from "@/components/EditableField";
import CandidateTimeline from "@/components/CandidateTimeline";
import CandidateServiceStatusModal from "@/components/CandidateServiceStatusModal";
import EvaluationFullFormModal from "@/components/EvaluationFullFormModal";
import OwnershipTimeline from "@/components/OwnershipTimeline";
import { useCanRecord, useCan } from "@/lib/authz";
import {
  ArrowLeft, CalendarBlank, Envelope, Phone, LinkedinLogo, Note, User, Briefcase,
  IdentificationCard, ClockCounterClockwise, FileText, ChatCircle, Trophy, ShieldCheck,
  Handshake, ChartLineUp, Gear, Trash, MapPin, Sparkle, PaperPlaneTilt,
} from "@phosphor-icons/react";

const PRIORITY_CLS = { Hot: "badge-hot", Normal: "badge-normal", Urgent: "badge-urgent" };
const STATUS_CLS = {
  Active: "badge-active", Hold: "badge-pause", Resumed: "badge-active", Pause: "badge-pause",
  "On Hold": "badge-pause",
  Completed: "badge-completed", Cancelled: "badge-cancelled",
  "Service Completed": "badge-completed",
  "Service Terminated": "badge-cancelled",
  "Service Discontinued": "badge-cancelled",
};

const CLOSED_STATUSES = new Set(["Service Completed", "Service Terminated", "Service Discontinued", "Completed", "Cancelled"]);

const VISA_OPTIONS = ["USC", "GC", "H1B", "H4-EAD", "L2-EAD", "OPT", "STEM-OPT", "CPT", "TN", "GC-EAD", "Other"];
const SERVICE_STATUS_OPTIONS = [
  "Active", "On Hold", "Hold", "Resumed", "Pause",
  "Service Completed", "Service Terminated", "Service Discontinued",
  "Completed", "Cancelled",
];
const PRIORITY_OPTIONS = ["Hot", "Normal", "Urgent"];
const MARKETING_TYPE_OPTIONS = ["W2", "C2C", "1099", "Both"];

const TABS = [
  { key: "overview", label: "Overview", icon: User },
  { key: "recommended", label: "Recommended Jobs", icon: Sparkle },
  { key: "applications", label: "Applications", icon: PaperPlaneTilt },
  { key: "timeline", label: "Service Timeline", icon: ClockCounterClockwise },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "evaluations", label: "Evaluations", icon: ChartLineUp },
  { key: "activity", label: "Submissions & Interviews", icon: Handshake },
  { key: "portal", label: "Portal & Credentials", icon: ShieldCheck, leaderOnly: true },
  { key: "ai", label: "AI", icon: ChatCircle },
];

export default function CandidateDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [offers, setOffers] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [tab, setTab] = useState("overview");
  const canApply = user?.role !== "executive";
  const isLeader = user?.role === "admin" || user?.role === "team_lead";
  // Phase A/B/C — dual check on record. `useCanRecord` returns true only when
  // BOTH the role has "candidate.edit" AND the record's assigned_employee_id
  // is inside the user's downline visibility. Admin/HR bypass hierarchy.
  const canEdit = useCanRecord("candidate.edit", data?.candidate?.assigned_employee_id);
  const canDelete = useCanRecord("candidate.delete", data?.candidate?.assigned_employee_id);
  // UAT Batch 1 — who may close/reopen a service (same rule applies).
  const canChangeServiceStatus = useCanRecord("candidate.edit", data?.candidate?.assigned_employee_id)
    && ["admin", "hr", "team_lead", "sdl"].includes(user?.role);
  // Phase 11 Beat 1 — Group Lead / Manager can start a new evaluation from dossier.
  // Reuse `canEdit` hook result (already computed above) — never call a hook conditionally.
  const canStartEvaluation = ["admin", "team_lead", "sdl"].includes(user?.role) && canEdit;
  const [showServiceStatusModal, setShowServiceStatusModal] = useState(false);
  const [evalModalId, setEvalModalId] = useState(null);
  const startEvaluation = async () => {
    try {
      const r = await api.post(`/candidates/${id}/evaluations/start-from-dossier`);
      setEvalModalId(r.data.evaluation_id);
      toast.success("Evaluation started — form pre-filled from dossier.");
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to start evaluation");
    }
  };

  const { items: placementFields } = useMasterData("placement_field");
  const { items: technologies } = useMasterData("technology");
  const { items: marketingTypes } = useMasterData("marketing_type");

  const [loadError, setLoadError] = useState(null);

  const reload = useCallback(() => {
    setLoadError(null);
    api.get(`/dashboard/candidate/${id}`)
      .then((r) => setData(r.data))
      .catch((ex) => {
        const code = ex?.response?.status;
        if (code === 403) setLoadError({ kind: "forbidden", msg: "You don't have permission to view this candidate." });
        else if (code === 404) setLoadError({ kind: "not_found", msg: "This candidate could not be found." });
        else setLoadError({ kind: "error", msg: ex?.response?.data?.detail || "Failed to load candidate details." });
      });
    api.get(`/assignments/candidate/${id}`).then((r) => setHistory(r.data)).catch(() => setHistory([]));
    api.get(`/interviews?candidate_id=${id}`).then((r) => setInterviews(r.data)).catch(() => setInterviews([]));
    api.get(`/offers?candidate_id=${id}`).then((r) => setOffers(r.data)).catch(() => setOffers([]));
    api.get(`/evaluations?candidate_id=${id}`).then((r) => setEvaluations(r.data)).catch(() => setEvaluations([]));
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  const onCandUpdated = (nextCand) => {
    setData((d) => (d ? { ...d, candidate: { ...d.candidate, ...nextCand } } : d));
  };

  const c = data?.candidate;
  const src = c?.field_sources || {};

  const placementOptions = useMemo(
    () => (placementFields.length ? placementFields.map((m) => m.value) : []),
    [placementFields]
  );

  if (loadError) {
    const tone = loadError.kind === "forbidden" ? "amber"
               : loadError.kind === "not_found" ? "slate"
               : "red";
    const toneMap = {
      amber: "bg-amber-50 border-amber-200 text-amber-800",
      slate: "bg-slate-50 border-slate-200 text-slate-700",
      red: "bg-red-50 border-red-200 text-red-800",
    }[tone];
    return (
      <div className="space-y-4" data-testid="candidate-detail-error">
        <Link to="/candidates" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-slate-800">
          <ArrowLeft size={14} /> Back to Candidates
        </Link>
        <div className={`border rounded-lg p-6 ${toneMap}`} data-testid={`candidate-detail-error-${loadError.kind}`}>
          <div className="text-base font-semibold mb-1">
            {loadError.kind === "forbidden" ? "Access denied"
             : loadError.kind === "not_found" ? "Candidate not found"
             : "Unable to load candidate"}
          </div>
          <div className="text-sm">{loadError.msg}</div>
          {loadError.kind === "error" && (
            <button onClick={reload} data-testid="candidate-detail-retry"
                    className="mt-3 text-xs px-3 py-1.5 rounded border border-red-300 bg-white hover:bg-red-100 text-red-800 font-medium">
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }
  if (!data || !c) return <div className="text-slate-500" data-testid="candidate-detail-loading">Loading…</div>;

  const visibleTabs = TABS.filter((t) => !t.leaderOnly || isLeader);
  const Stat = ({ label, value }) => (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="candidate-detail">
      <Link to="/candidates" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-slate-800">
        <ArrowLeft size={14} /> Back to Candidates
      </Link>

      {/* ===== Header ===== */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-slate-200 grid place-items-center font-semibold text-slate-700">
              {(c.full_name || "??").slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-semibold" data-testid="candidate-name">{c.full_name || "Unnamed candidate"}</h2>
              <div className="text-sm text-slate-500">{c.preferred_role || "Candidate"} · {c.placement_field || "—"}</div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1.5"><Envelope size={14} /> {c.email}</span>
            {c.phone && <span className="inline-flex items-center gap-1.5"><Phone size={14} /> {c.phone}</span>}
            {c.linkedin && <a href={c.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-sky-700"><LinkedinLogo size={14} /> LinkedIn</a>}
            {c.service_start_date && <span className="inline-flex items-center gap-1.5"><CalendarBlank size={14} /> Started {c.service_start_date}</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-xs px-2.5 py-1 rounded-full ${PRIORITY_CLS[c.priority]}`}>Priority: {c.priority}</span>
          <span className={`text-xs px-2.5 py-1 rounded-full ${STATUS_CLS[c.service_status] || "bg-slate-100 text-slate-600"}`} data-testid="service-status-badge">
            {c.service_status}
          </span>
          {CLOSED_STATUSES.has(c.service_status) && (
            <span className="text-[10px] uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5" data-testid="service-closed-badge">
              Read-Only — Service Closed
            </span>
          )}
          <div className="text-xs text-slate-500">Days remaining · <b className="text-slate-800">{c.days_remaining}</b></div>
          <div className="text-xs text-slate-500">Assigned · <b className="text-slate-800">{data.assigned_employee_name || "—"}</b></div>
          {c.revised_end_date && c.revised_end_date !== c.expected_end_date && (
            <div className="text-xs text-amber-700">Extended by {c.total_hold_days}d · ends {c.revised_end_date}</div>
          )}
          {canApply && (<div className="pt-2"><HoldResumeControl candidate={c} onChange={reload} /></div>)}
          {canChangeServiceStatus && (
            <button onClick={() => setShowServiceStatusModal(true)}
                    data-testid="change-service-status-btn"
                    className="mt-2 text-xs px-3 py-1.5 rounded-md bg-slate-800 text-white hover:bg-slate-900 inline-flex items-center gap-1.5">
              <Gear size={12} /> Change Service Status
            </button>
          )}
          {canStartEvaluation && (
            <button onClick={startEvaluation}
                    data-testid="start-evaluation-btn"
                    className="mt-1 text-xs px-3 py-1.5 rounded-md bg-sky-700 text-white hover:bg-sky-800 inline-flex items-center gap-1.5">
              <Trophy size={12} /> Start Evaluation
            </button>
          )}
          {canDelete && (
            <button onClick={async () => {
                      if (!window.confirm(`Delete candidate "${c.full_name}"? This action cannot be undone.`)) return;
                      try {
                        await api.delete(`/candidates/${id}`);
                        toast.success("Candidate deleted");
                        nav("/candidates", { replace: true });
                      } catch (ex) {
                        toast.error(ex.response?.data?.detail || "Failed to delete candidate");
                      }
                    }}
                    data-testid="candidate-delete-btn"
                    className="mt-1 text-xs px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1.5">
              <Trash size={12} /> Delete Candidate
            </button>
          )}
        </div>
      </div>

      {showServiceStatusModal && (
        <CandidateServiceStatusModal
          candidate={c}
          onClose={() => setShowServiceStatusModal(false)}
          onChanged={reload}
        />
      )}

      {evalModalId && (
        <EvaluationFullFormModal
          evaluationId={evalModalId}
          onClose={() => setEvalModalId(null)}
          onSubmitted={reload}
        />
      )}

      {/* ===== Ownership Timeline (append-only audit strip) ===== */}
      <OwnershipTimeline candidateId={id} />

      {/* ===== Stat row ===== */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Applications" value={data.applications_total} />
        <Stat label="Career Portal" value={data.career_portal} />
        <Stat label="Easy Apply" value={data.easy_apply} />
        <Stat label="Cold Emails" value={data.cold_emails} />
        <Stat label="Interviews" value={data.interviews} />
        <Stat label="Offers" value={data.offers} />
      </div>

      {/* Phase 13 Beat 4 v2 — persistent Job Workspace CTA visible on every tab.
          Recommended Jobs + Applications panels below remain fully functional
          throughout Business UAT; this is an additive shortcut, not a replacement. */}
      <div className="bg-gradient-to-r from-indigo-50 to-sky-50 border border-indigo-100 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3"
           data-testid="candidate-workspace-cta">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-white border border-indigo-200 grid place-items-center text-indigo-600 shrink-0">
            <Briefcase size={18} weight="fill" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Continue in the Job Workspace</div>
            <p className="text-xs text-slate-600 mt-0.5">
              Search recommended, shared, and manual jobs · review match explanations · initiate and track applications — all in one place, with this candidate pre-selected.
            </p>
          </div>
        </div>
        <Link to={`/job-workspace?candidate=${id}`}
               data-testid="candidate-open-workspace-header"
               className="text-xs inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium shrink-0">
          Open Job Workspace ▸
        </Link>
      </div>

      {/* ===== Tabs ===== */}
      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto">
        {visibleTabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`px-3 py-2 text-sm border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${tab === t.key ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* ===== Overview tab (editable) ===== */}
      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Personal & Contact" icon={User}>
            <EditableField candidateId={id} field="full_name" label="Full name" value={c.full_name} source={src.full_name} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="email" label="Email" value={c.email} type="email" source={src.email} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="phone" label="Phone" value={c.phone} type="tel" source={src.phone} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="whatsapp" label="WhatsApp" value={c.whatsapp} type="tel" source={src.whatsapp} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="linkedin" label="LinkedIn" value={c.linkedin} type="url" source={src.linkedin} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="current_city" label="Current city" value={c.current_city} source={src.current_city} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="country" label="Country" value={c.country} source={src.country} canEdit={canEdit} onUpdated={onCandUpdated} />
          </Section>

          <Section title="Placement & Skills" icon={Briefcase}
                   badge={(src.placement_field === "evaluation" || src.skills === "evaluation") ? "synced from evaluation" : null}>
            <EditableField candidateId={id} field="placement_field" label="Placement field" value={c.placement_field} type={placementOptions.length ? "select" : "text"} options={placementOptions} source={src.placement_field} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="preferred_role" label="Preferred role" value={c.preferred_role} source={src.preferred_role} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="total_experience_years" label="Experience (years)" value={c.total_experience_years} type="number" source={src.total_experience_years} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="skills" label="Skills" value={c.skills} type="list" source={src.skills} canEdit={canEdit} onUpdated={onCandUpdated} hint="Comma-separated" />
            <EditableField candidateId={id} field="technologies" label="Technologies" value={c.technologies} type="list" source={src.technologies} canEdit={canEdit} onUpdated={onCandUpdated} hint="Comma-separated" />
            <EditableField candidateId={id} field="preferred_locations" label="Preferred locations" value={c.preferred_locations} type="list" source={src.preferred_locations} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="preferred_job_titles" label="Preferred job titles" value={c.preferred_job_titles} type="list" source={src.preferred_job_titles} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="resume_keywords" label="Resume keywords" value={c.resume_keywords} type="list" source={src.resume_keywords} canEdit={canEdit} onUpdated={onCandUpdated} />
          </Section>

          {/* Phase 13 Beat 2 — Location & Arrangement (matching engine inputs) */}
          <Section title="Location & Work Arrangement" icon={MapPin}
                   badge={<span className="text-[10px] text-slate-500">Matching engine inputs</span>}>
            <EditableField candidateId={id} field="preferred_states" label="Preferred states" value={c.preferred_states} type="list" source={src.preferred_states} canEdit={canEdit} onUpdated={onCandUpdated} hint="Comma-separated (e.g. TX, CA)" />
            <EditableField candidateId={id} field="preferred_cities" label="Preferred cities" value={c.preferred_cities} type="list" source={src.preferred_cities} canEdit={canEdit} onUpdated={onCandUpdated} hint="Comma-separated (e.g. Austin, Dallas)" />
            <EditableField candidateId={id} field="willing_to_relocate" label="Willing to relocate?" value={c.willing_to_relocate} type="bool" source={src.willing_to_relocate} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="relocation_locations" label="Relocation locations" value={c.relocation_locations} type="list" source={src.relocation_locations} canEdit={canEdit} onUpdated={onCandUpdated} hint="Only if willing to relocate" />
            <div className="text-[11px] text-slate-500 mt-1 col-span-full">
              <b>Note:</b> Remote / Hybrid / On-site preferences live on the candidate's Portal Dossier (<code>dossier.personal_info</code>). Candidates can update them via a Change Request under Location &amp; Work Preferences.
            </div>
          </Section>

          <Section title="Visa & Identity" icon={IdentificationCard}>
            <EditableField candidateId={id} field="visa_status" label="Visa status" value={c.visa_status} type="select" options={VISA_OPTIONS} source={src.visa_status} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="ssn_last4" label="SSN (last 4 only)" value={c.ssn_last4} source={src.ssn_last4} canEdit={isLeader} onUpdated={onCandUpdated} hint="Only last four digits are stored." />
            <EditableField candidateId={id} field="date_of_birth" label="Date of birth" value={c.date_of_birth} type="date" source={src.date_of_birth} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="nationality" label="Nationality" value={c.nationality} source={src.nationality} canEdit={canEdit} onUpdated={onCandUpdated} />
          </Section>

          <Section title="Service" icon={CalendarBlank}>
            <EditableField candidateId={id} field="service_status" label="Service status" value={c.service_status} type="select" options={SERVICE_STATUS_OPTIONS} source={src.service_status} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="service_type" label="Service type" value={c.service_type} source={src.service_type} canEdit={canEdit} onUpdated={onCandUpdated} hint="Drives auto-calc of expected end date" />
            <EditableField candidateId={id} field="priority" label="Priority" value={c.priority} type="select" options={PRIORITY_OPTIONS} source={src.priority} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="service_start_date" label="Service start" value={c.service_start_date} type="date" source={src.service_start_date} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="program_duration_days" label="Duration (days)" value={c.program_duration_days} type="number" source={src.program_duration_days} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="expected_end_date" label="Expected end (auto)" value={c.expected_end_date} type="date" source={src.expected_end_date} canEdit={false} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="deadline_date" label="Deadline" value={c.deadline_date} type="date" source={src.deadline_date} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="enrollment_date" label="Enrollment date" value={c.enrollment_date} type="date" source={src.enrollment_date} canEdit={canEdit} onUpdated={onCandUpdated} />
          </Section>

          <Section title="Marketing" icon={Envelope}>
            <EditableField candidateId={id} field="marketing_email" label="Marketing email" value={c.marketing_email} type="email" source={src.marketing_email} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="marketing_type" label="Marketing type" value={c.marketing_type} type={marketingTypes.length ? "select" : "select"} options={marketingTypes.length ? marketingTypes.map((m) => m.value) : MARKETING_TYPE_OPTIONS} source={src.marketing_type} canEdit={canEdit} onUpdated={onCandUpdated} />
            <EditableField candidateId={id} field="source_of_hire" label="Source of hire" value={c.source_of_hire} source={src.source_of_hire} canEdit={canEdit} onUpdated={onCandUpdated} />
          </Section>

          <Section title="Notes" icon={Note}>
            <EditableField candidateId={id} field="evaluation_notes" label="Evaluation notes / remarks" value={c.evaluation_notes} type="textarea" source={src.evaluation_notes} canEdit={canEdit} onUpdated={onCandUpdated} />
          </Section>

          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-1.5"><UserCircleIcon /> Assignment History</h3>
            <div className="space-y-3">
              {history.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No assignments recorded.</div>}
              {history.map((h) => (
                <div key={h.id} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-sky-500 mt-1.5" />
                  <div className="text-sm">
                    <div><b>{h.assigned_to_name}</b> assigned by {h.assigned_by_name}</div>
                    <div className="text-xs text-slate-500">{new Date(h.assignment_date).toLocaleString()} {h.reason ? `· ${h.reason}` : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== Timeline tab ===== */}
      {tab === "timeline" && <CandidateTimeline candidateId={id} />}

      {/* ===== Documents tab ===== */}
      {tab === "documents" && <Documents candidateId={id} />}

      {/* ===== Evaluations tab ===== */}
      {tab === "evaluations" && (
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="evaluations-list">
          <h3 className="font-semibold mb-3">Evaluations</h3>
          {evaluations.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No evaluations yet.</div>}
          <div className="space-y-3">
            {evaluations.map((e) => (
              <Link key={e.id} to="/evaluations" className="block border border-slate-100 rounded-md p-3 hover:bg-slate-50">
                <div className="text-sm font-medium">
                  {e.evaluation_date} · {e.evaluation_time} · {e.meeting_platform}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Status: <b>{e.status}</b>
                  {e.form?.overall_evaluation && ` · ${e.form.overall_evaluation.slice(0, 100)}`}
                </div>
                {e.form?.placement_field && <div className="text-xs text-sky-700 mt-0.5">Placement: {e.form.placement_field}</div>}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ===== Activity tab (submissions, interviews, offers) ===== */}
      {tab === "activity" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="font-semibold mb-3">Recent Submissions</h3>
            <div className="divide-y divide-slate-100">
              {data.recent_applications.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No submissions yet.</div>}
              {data.recent_applications.map((a) => (
                <div key={a.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium text-slate-800">{a.job_title} · <span className="text-slate-500">{a.company}</span></div>
                    <div className="text-xs text-slate-500">{a.app_type} · {a.portal_label} · {new Date(a.submission_time).toLocaleString()}</div>
                  </div>
                  <a href={a.application_url} target="_blank" rel="noreferrer" className="text-xs text-sky-700 hover:underline">Link</a>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="font-semibold mb-3">Interviews</h3>
            <div className="divide-y divide-slate-100">
              {interviews.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">None.</div>}
              {interviews.map((i) => (
                <div key={i.id} className="py-2 text-sm">
                  <div className="font-medium">{i.company} · {i.job_title}</div>
                  <div className="text-xs text-slate-500">{i.interview_date} · {i.round} · <b>{i.status}</b></div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-1.5"><Trophy size={14} /> Offers</h3>
            <div className="divide-y divide-slate-100">
              {offers.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">None yet.</div>}
              {offers.map((o) => (
                <div key={o.id} className="py-2 text-sm">
                  <div className="font-medium">{o.company} · {o.job_title}</div>
                  <div className="text-xs text-slate-500">{o.offer_date} · {o.salary} · <b>{o.status}</b></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== Portal & credentials tab ===== */}
      {tab === "portal" && isLeader && (
        <div className="space-y-5">
          <CandidateCredentials candidateId={id} allowed={isLeader} />
          <CandidatePortalAccess candidateId={id} candidateEmail={c.email} />
        </div>
      )}

      {/* ===== Phase 13 Beat 3 — Recommended Jobs tab ===== */}
      {tab === "recommended" && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <a href={`/job-workspace?candidate=${id}`}
               data-testid="candidate-open-workspace-recommended"
               className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
              Open Job Workspace ▸
            </a>
          </div>
          <RecommendedJobsPanel
            candidateId={id}
            canApply={canEdit}
            onApplicationChanged={onCandUpdated}
          />
        </div>
      )}

      {/* ===== Phase 13 Beat 3 — Applications history tab ===== */}
      {tab === "applications" && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <a href={`/job-workspace?candidate=${id}&tab=applications`}
               data-testid="candidate-open-workspace-applications"
               className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
              Open Job Workspace ▸
            </a>
          </div>
          <CandidateApplicationsPanel candidateId={id} />
        </div>
      )}

      {/* ===== AI tab ===== */}
      {tab === "ai" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AIPrioritySuggest candidateId={id} canApply={canApply} currentPriority={c.priority}
                             onApplied={(p) => setData((d) => ({ ...d, candidate: { ...d.candidate, priority: p } }))} />
          <AskAI candidateId={id} />
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children, badge }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-1.5">{Icon && <Icon size={14} />} {title}</h3>
        {badge && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">{badge}</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{children}</div>
    </div>
  );
}

function UserCircleIcon() {
  return <User size={14} />;
}
